import { useSelector } from 'react-redux';
import { Home, Gate, Badge, Form, Maintenance, Training, Permit, Document, Lease, Safety, Invoice, Inspection } from './icons';

export const useAllModules = (items, path) => {
  const { someModulePermission, avpModulePermission, leaseDocumentPermission } = useSelector(s => s.auth);

  const {
    profile,
    isMaintenanceEnabled,
    isInspectionsEnabled,
    invoiceEnabled,
    documentEnabled,
    trainingEnabled,
    smsSafetyPromEnabled,
    isSharedDcosEnabled,
    isMyFormsEnabled
  } = items;

  const airportSettings = profile?.portal_airport?.airport_settings || {};

  const maintenanceTitle = airportSettings?.public_nawo_nomenclature?.length > 0
      ? airportSettings?.public_nawo_nomenclature
      : 'Maintenance Requests';

  const permissions = profile?.permissions || {};

  const {
    permitEnabled = false,
    gateEnabled: gateModuleEnabled = false,
    badgeEnabled: badgeModuleEnabled = false
  } = someModulePermission || {};

  const adpEnabled = !!permissions?.driver_permit?.length && permitEnabled;
  const avpEnabled = !!permissions?.vehicle_permit?.length && permitEnabled;
  const badgeEnabled = !!permissions?.signitory_review && badgeModuleEnabled;
  const gateEnabled = !!permissions?.gate_management?.length && gateModuleEnabled;
  const grossRecieptsEnabled = !!permissions?.enable_gross_receipts;

  const isOnlyAirfieldWorker = Object.keys(permissions || {}).length === 1
    && Object.keys(permissions || {})?.[0] === 'airfield_worker_driver_permit';

  const allModules = [
    {
      menu_key: 'airport_portal',
      id: 'topbar.menu.airport.portal',
      name: 'Airport Portal',
      icon: Home,
      isActive: false,
      subMenu: [
        {
          id: 'topbar.menu.home',
          name: 'Home',
          pathname: '/',
          permission: true,
          icon: Home
        },
        {
          id: 'todo.my_tasks',
          name: 'My Tasks',
          pathname: '/my_tasks',
          permission: true,
          icon: Form
        },
        {
          id: 'todo.completed_tasks',
          name: 'Completed Tasks',
          pathname: '/completed_tasks',
          permission: true,
          icon: Form
        }
      ]
    },

    {
      menu_key: 'operations',
      id: 'topbar.menu.operations',
      name: 'Operations',
      icon: Gate,
      isActive: false,
      subMenu: [
        {
          id: 'topbar.Manage_Gates',
          name: 'Manage Gates',
          pathname: '/gateManagement',
          permission: gateEnabled,
          icon: Gate
        },
        {
          id: 'topbar.gate_allocation',
          name: 'Gate Allocation',
          pathname: '/gate_allocation',
          permission: gateEnabled,
          icon: Gate
        },
        {
          id: 'topbar.gate_change',
          name: 'Gate Change Request',
          pathname: '/gate_change',
          permission: gateEnabled,
          icon: Gate
        }
      ]
    },

    {
      menu_key: 'maintenance',
      id: 'topbar.menu.maintanance',
      name: 'Maintenance',
      icon: Maintenance,
      isActive: false,
      subMenu: [
        {
          id: 'topbar.menu.work.orders',
          name: 'Work Orders',
          pathname: '/maintenance',
          permission: isMaintenanceEnabled,
          values: maintenanceTitle,
          icon: Maintenance
        }
      ]
    },

    {
      menu_key: 'lease_documents',
      id: 'topbar.menu.lease.and.documents',
      name: 'Leases & Documents',
      icon: Invoice,
      isActive: false,
      subMenu: [
        {
          id: 'topbar.Documents',
          name: 'Lease Documents',
          pathname: '/document',
          permission: documentEnabled && (leaseDocumentPermission || avpModulePermission),
          icon: Lease
        },
        {
          id: 'topbar.shared_doc',
          name: 'Shared Documents',
          pathname: '/documents',
          permission: isSharedDcosEnabled,
          icon: Document
        }
      ]
    },

    {
      menu_key: 'compliance_safety',
      id: 'topbar.menu.compliance.and.safety',
      name: 'Compliance & Safety',
      icon: Inspection,
      isActive: false,
      subMenu: [
        {
          id: 'topbar.Badging',
          name: 'Badging',
          pathname: '/badging/List',
          permission: badgeEnabled,
          icon: Badge
        },
        {
          id: 'topbar.inspections',
          name: 'Inspections',
          pathname: '/myInspections',
          permission: isInspectionsEnabled,
          icon: Inspection
        },
        {
          id: 'topbar.Safety_Promotions',
          name: 'Safety Promotions',
          pathname: '/sms/safety_promotion',
          permission: smsSafetyPromEnabled,
          icon: Safety
        }
      ]
    },

    {
      menu_key: 'training',
      id: 'topbar.menu.training',
      name: 'Training',
      icon: Training,
      isActive: false,
      subMenu: [
        {
          id: 'topbar.My_Trainings',
          name: 'My Trainings',
          pathname: '/training',
          permission: trainingEnabled,
          icon: Training,
          isIncludeInDriverPermit: true
        }
      ]
    },

    {
      menu_key: 'forms_approvals',
      id: 'topbar.menu.forms.approvals',
      name: 'Forms & Approvals',
      icon: Form,
      isActive: false,
      subMenu: [
        {
          id: 'topbar.forms.library',
          name: 'Forms Library',
          pathname: '/myforms',
          permission: isMyFormsEnabled,
          icon: Form,
          isIncludeInDriverPermit: true
        },
        {
          id: 'topbar.submitted.forms',
          name: 'Submitted Forms',
          pathname: '/form-submissions',
          permission: isMyFormsEnabled,
          icon: Form,
          isIncludeInDriverPermit: true
        },
        {
          id: 'topbar.form.approvals',
          name: 'Form Approvals',
          pathname: '/form-approvals',
          permission: isMyFormsEnabled,
          icon: Form,
          isIncludeInDriverPermit: true
        }
      ]
    },

    {
      menu_key: 'permits',
      id: 'topbar.menu.permit.management',
      name: 'Permits',
      icon: Permit,
      isActive: false,
      subMenu: [
        {
          id: ' ',
          name: profile?.permit_management?.driver_permit || 'ADP',
          pathname: isOnlyAirfieldWorker ? '/adp/myapplications' : '/adp/logs',
          permission: adpEnabled || isOnlyAirfieldWorker,
          icon: Permit,
          isIncludeInDriverPermit: true
        },
        {
          id: ' ',
          name: profile?.permit_management?.vehicle_permit || 'AVP',
          pathname: '/avp/logs',
          permission: avpEnabled,
          icon: Permit
        }
      ]
    },

    {
      menu_key: 'financials',
      id: 'topbar.menu.financials',
      name: 'Financials',
      icon: Lease,
      isActive: false,
      subMenu: [
        {
          id: 'topbar.Invoices',
          name: 'Invoices',
          pathname: '/invoice',
          permission: invoiceEnabled,
          icon: Invoice
        },
        {
          id: 'topbar.usage.fee.reporting',
          name: 'Usage & Fee Reporting',
          pathname: '/gross_receipts/list',
          permission: grossRecieptsEnabled,
          icon: Safety
        }
      ]
    }
  ].map(item => ({
    ...item,
    subMenu: item.subMenu.filter((sub) => {
      if (!sub.permission) return false;

      if (isOnlyAirfieldWorker) {
        return sub.isIncludeInDriverPermit;
      }

      return true;
    })
  }))
  .filter(item => item.subMenu.length > 0)
  .map((i, index) => ({
    ...i,
    isActive: index === 0
  }));

  const isSubMenuActive = (currentPath, subPath) => {
    if (subPath === '/') return currentPath === '/';
    return currentPath === subPath || currentPath.startsWith(`/${subPath.split('/').filter(Boolean)[0]}/`);
  };

  return allModules.map(module => ({
    ...module,
    isActive: module.subMenu.some(sub => isSubMenuActive(path, sub.pathname))
  }));
};
